"use client";

import { c as _c } from "react/compiler-runtime";
import { EllipsisTooltip, Modal, ModalContent, ScopeIcon, Skeleton, Spinner, Tooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { CloseIcon } from "@dc/ui/vectors";
import { Command } from "cmdk";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { type FC, useEffect, useRef, useState } from "react";
import { MagniferLinear, QuestionCircleLinear, WidgetLinear } from "solar-icon-set";
import { queueHighlight } from "@/hooks/useScrollToHighlight";
import type { SearchResultItem } from "./GlobalSearch.types";
import { useGlobalSearchQuery } from "./useGlobalSearchQuery";
interface GlobalSearchProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  navigation: <WidgetLinear className="text-default-400" size={18} />,
  projects: <ScopeIcon type="PROJECT" size="sm" />,
  folders: <ScopeIcon type="FOLDER" size="sm" />,
  files: <ScopeIcon type="FILE" size="sm" />,
  articles: <QuestionCircleLinear className="text-default-400" size={18} />
};
export const GlobalSearch: FC<GlobalSearchProps> = t0 => {
  const $ = _c(64);
  const {
    isOpen,
    onOpenChange
  } = t0;
  const t = useTranslations("global_search");
  const router = useRouter();
  const [query, setQuery] = useState("");
  const pylonCleanupRef = useRef(null);
  const {
    navigation,
    projects,
    folders,
    files,
    articles,
    featuredArticles,
    isLoading,
    isLoadingArticles,
    hasResults
  } = useGlobalSearchQuery(isOpen ? query : "", isOpen);
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        window.Pylon?.("hide");
      }
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== onOpenChange) {
    t3 = () => {
      onOpenChange(false);
    };
    $[3] = onOpenChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleClose = t3;
  const handleSelect = item => {
    if (item.category === "articles") {
      handleClose();
      if (window.Pylon) {
        setTimeout(() => {
          window.Pylon?.("showKnowledgeBaseArticle", item.id);
          const chatWindow = document.getElementById("pylon-chat-window");
          if (!chatWindow) {
            return;
          }
          pylonCleanupRef.current?.();
          const timer = {};
          const cleanup = () => {
            clearTimeout(timer.id);
            resizeObserver.disconnect();
            document.removeEventListener("click", onClickOutside);
            document.removeEventListener("keydown", onEscape);
            pylonCleanupRef.current = null;
            window.Pylon?.("hide");
          };
          pylonCleanupRef.current = cleanup;
          const onClickOutside = e => {
            if (!chatWindow.contains(e.target as Node)) {
              cleanup();
            }
          };
          document.addEventListener("click", onClickOutside);
          const onEscape = e_0 => {
            if (e_0.key === "Escape") {
              cleanup();
            }
          };
          document.addEventListener("keydown", onEscape);
          let articleHeight = 0;
          const resizeObserver = new ResizeObserver(entries => {
            const height = entries[0].contentRect.height;
            if (articleHeight === 0 && height > 600) {
              articleHeight = height;
            } else {
              if (articleHeight > 0 && height < articleHeight * 0.8) {
                cleanup();
              }
            }
          });
          resizeObserver.observe(chatWindow);
          timer.id = setTimeout(cleanup, 300000);
        }, 300);
      } else {
        window.open(item.href, "_blank", "noopener,noreferrer");
      }
      return;
    }
    handleClose();
    if (item.highlightId) {
      queueHighlight(item.highlightId);
    }
    router.push(item.href);
  };
  const renderGroup = (label, items) => {
    if (items.length === 0) {
      return null;
    }
    return <Command.Group heading={<span className="font-semibold text-default-600 text-xs">{label}</span>}>{items.map(item_0 => <Command.Item key={item_0.id} className={cn("flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5", "text-default-700 aria-selected:bg-default-100")} value={`${item_0.category}-${item_0.id}`} onSelect={() => handleSelect(item_0)}>{item_0.icon ?? CATEGORY_ICONS[item_0.category]}<div className="flex min-w-0 flex-col"><EllipsisTooltip content={item_0.title} as="span" className="text-sm" />{item_0.subtitle && <span className="truncate text-default-500 text-xs">{item_0.subtitle}</span>}</div></Command.Item>)}</Command.Group>;
  };
  const showEmptyState = query.length === 0 && navigation.length === 0 && featuredArticles.length === 0 && !isLoadingArticles;
  const showNoResults = query.length > 0 && !hasResults && !isLoading;
  const T0 = Modal;
  const t4 = true;
  const t5 = true;
  const t6 = "blur";
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      base: cn("mt-[20vh]", "border-small", "border-default-200", "dark:border-default-100", "bg-background")
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== isOpen) {
    t8 = {
      onAnimationComplete: () => {
        if (!isOpen) {
          setQuery("");
        }
      }
    };
    $[6] = isOpen;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = "top";
  const t10 = "inside";
  const t11 = "xl";
  const T1 = ModalContent;
  const T2 = Command;
  let t12;
  if ($[8] !== t) {
    t12 = t("label");
    $[8] = t;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const t13 = false;
  let t14;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn("flex w-full items-center px-4", "border-default-400/50 border-b dark:border-default-100");
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  let t15;
  if ($[11] !== isLoading) {
    t15 = isLoading ? <Spinner size="sm" className="text-default-400" /> : <MagniferLinear className="text-default-400 text-lg" size={20} />;
    $[11] = isLoading;
    $[12] = t15;
  } else {
    t15 = $[12];
  }
  let t16;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("h-14 w-full px-2 font-sans text-lg", "rounded-none bg-transparent outline-hidden", "text-default-700 placeholder-default-500", "dark:text-default-500 dark:placeholder:text-default-300");
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  let t17;
  if ($[14] !== t) {
    t17 = t("placeholder");
    $[14] = t;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  let t18;
  if ($[16] !== query || $[17] !== t17) {
    t18 = <Command.Input autoFocus={true} className={t16} placeholder={t17} value={query} onValueChange={setQuery} />;
    $[16] = query;
    $[17] = t17;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] !== t) {
    t19 = t("close_with_shortcut");
    $[19] = t;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== t) {
    t20 = t("close");
    $[21] = t;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <CloseIcon />;
    $[23] = t21;
  } else {
    t21 = $[23];
  }
  let t22;
  if ($[24] !== handleClose || $[25] !== t20) {
    t22 = <button type="button" className="ml-2 flex size-7 cursor-pointer items-center justify-center rounded-full text-icon-neutral-onMatch-usual transition-colors hover:bg-icon-neutral-onMatch-usual/10 active:bg-icon-neutral-onMatch-usual/50" onClick={handleClose} aria-label={t20}>{t21}</button>;
    $[24] = handleClose;
    $[25] = t20;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== t19 || $[28] !== t22) {
    t23 = <Tooltip content={t19} placement="bottom" color="foreground">{t22}</Tooltip>;
    $[27] = t19;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== t15 || $[31] !== t18 || $[32] !== t23) {
    t24 = <div className={t14}>{t15}{t18}{t23}</div>;
    $[30] = t15;
    $[31] = t18;
    $[32] = t23;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  const t25 = Command;
  const t26 = "max-h-[40vh] overflow-y-auto p-2";
  let t27;
  if ($[34] !== query || $[35] !== showNoResults || $[36] !== t) {
    t27 = showNoResults && <Command.Empty><div className="flex h-32 flex-col items-center justify-center text-center"><p className="text-default-500">{t("no_results", {
            query
          })}</p></div></Command.Empty>;
    $[34] = query;
    $[35] = showNoResults;
    $[36] = t;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  let t28;
  if ($[38] !== showEmptyState || $[39] !== t) {
    t28 = showEmptyState && <div className="flex h-32 flex-col items-center justify-center text-center"><p className="text-default-400">{t("empty_state")}</p></div>;
    $[38] = showEmptyState;
    $[39] = t;
    $[40] = t28;
  } else {
    t28 = $[40];
  }
  const t29 = query.length === 0 && navigation.length > 0 && renderGroup(t("quick_links"), navigation);
  const t30 = query.length === 0 && (isLoadingArticles || featuredArticles.length > 0) && <Command.Group heading={<span className="font-semibold text-default-600 text-xs">{t("popular_articles")}</span>}>{isLoadingArticles ? [0, 1, 2, 3, 4].map(_temp) : featuredArticles.map(item_1 => <Command.Item key={item_1.id} className={cn("flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5", "text-default-700 aria-selected:bg-default-100")} value={`${item_1.category}-${item_1.id}`} onSelect={() => handleSelect(item_1)}>{item_1.icon ?? CATEGORY_ICONS[item_1.category]}<div className="flex min-w-0 flex-col"><EllipsisTooltip content={item_1.title} as="span" className="text-sm" /></div></Command.Item>)}</Command.Group>;
  const t31 = query.length > 0 && <>{renderGroup(t("categories.navigation"), navigation)}{renderGroup(t("categories.projects"), projects)}{renderGroup(t("categories.folders"), folders)}{renderGroup(t("categories.files"), files)}{renderGroup(t("categories.articles"), articles)}</>;
  let t32;
  if ($[41] !== t25.List || $[42] !== t27 || $[43] !== t28 || $[44] !== t29 || $[45] !== t30 || $[46] !== t31) {
    t32 = <t25.List className={t26}>{t27}{t28}{t29}{t30}{t31}</t25.List>;
    $[41] = t25.List;
    $[42] = t27;
    $[43] = t28;
    $[44] = t29;
    $[45] = t30;
    $[46] = t31;
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== T2 || $[49] !== t12 || $[50] !== t24 || $[51] !== t32) {
    t33 = <T2 label={t12} shouldFilter={t13}>{t24}{t32}</T2>;
    $[48] = T2;
    $[49] = t12;
    $[50] = t24;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== T1 || $[54] !== t33) {
    t34 = <T1>{t33}</T1>;
    $[53] = T1;
    $[54] = t33;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== T0 || $[57] !== handleClose || $[58] !== isOpen || $[59] !== onOpenChange || $[60] !== t34 || $[61] !== t7 || $[62] !== t8) {
    t35 = <T0 hideCloseButton={t4} skipBack={t5} backdrop={t6} classNames={t7} isOpen={isOpen} motionProps={t8} placement={t9} scrollBehavior={t10} size={t11} onClose={handleClose} onOpenChange={onOpenChange}>{t34}</T0>;
    $[56] = T0;
    $[57] = handleClose;
    $[58] = isOpen;
    $[59] = onOpenChange;
    $[60] = t34;
    $[61] = t7;
    $[62] = t8;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  return t35;
};
function _temp(i) {
  return <div key={`popular-article-skeleton-${i}`} className="py-2"><Skeleton className="h-6 rounded-md opacity-50" /></div>;
}